import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getDiscoverPosts, type PublicDiscoverPost } from '@/lib/api';
import { pickContent, pickTitle } from '@/lib/i18n-pick';
import { useTk, useLocale } from '@/lib/i18n';
import { useSelectedCity } from '@/lib/use-city';
import { BottomNav } from '@/components/BottomNav';
import { Button } from '@sabay/ui-kit';

interface LoadState {
  status: 'loading' | 'success' | 'error';
  items: PublicDiscoverPost[];
  errorMessage: string | null;
}

export function DiscoverPage(): React.ReactElement {
  const tk = useTk();
  const locale = useLocale();
  const navigate = useNavigate();
  const [cityCode] = useSelectedCity();
  const [query, setQuery] = useState('');
  const [state, setState] = useState<LoadState>({
    status: 'loading',
    items: [],
    errorMessage: null,
  });

  useEffect(() => {
    const ac = new AbortController();
    setState({ status: 'loading', items: [], errorMessage: null });
    getDiscoverPosts({ cityCode, pageSize: 30 }, ac.signal)
      .then((res) =>
        setState({ status: 'success', items: res.items, errorMessage: null }),
      )
      .catch((err: Error) => {
        if (ac.signal.aborted) return;
        setState({ status: 'error', items: [], errorMessage: err.message });
      });
    return () => ac.abort();
  }, [cityCode]);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredItems =
    normalizedQuery.length === 0
      ? state.items
      : state.items.filter((post) => {
          const title = pickTitle(post, locale).toLowerCase();
          const content = pickContent(post.contentI18n, locale).toLowerCase();
          const merchant = post.merchantNameZh.toLowerCase();
          return (
            title.includes(normalizedQuery) ||
            content.includes(normalizedQuery) ||
            merchant.includes(normalizedQuery)
          );
        });

  return (
    <main className="min-h-screen pb-24">
      <header className="sticky top-0 z-20 px-5 pt-5 pb-3 bg-clay-50/85 backdrop-blur-md space-y-1">
        <span className="eyebrow">Stories · Discover</span>
        <h1 className="display-heading text-[26px] leading-tight">
          {tk('discover.title')}
        </h1>
      </header>

      <div className="px-5 mt-4">
        <label className="sr-only" htmlFor="discover-search">
          {tk('discover.search.label')}
        </label>
        <input
          id="discover-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          aria-label={tk('discover.search.label')}
          placeholder={tk('discover.search.placeholder')}
          className="mb-4 w-full rounded-full border border-clay-200 bg-white px-4 py-3 text-sm text-clay-800 shadow-[0_1px_2px_rgb(20_17_13/0.04)] placeholder:text-clay-400"
        />

        {state.status === 'loading' ? (
          <div className="surface-card border-dashed p-12 text-center text-clay-500 text-sm">
            {tk('common.loading')}
          </div>
        ) : null}

        {state.status === 'error' ? (
          <div className="rounded-2xl border border-danger-500/40 bg-danger-500/10 p-4 text-sm text-danger-500">
            <p className="font-medium">{tk('common.error.generic')}</p>
            <p className="text-xs opacity-75 mt-1 break-all">{state.errorMessage}</p>
          </div>
        ) : null}

        {state.status === 'success' && filteredItems.length === 0 ? (
          <div className="surface-card border-dashed p-12 text-center text-clay-500 text-sm">
            {tk('discover.empty')}
          </div>
        ) : null}

        {state.status === 'success' && filteredItems.length > 0 ? (
          <ul className="space-y-4">
            {filteredItems.map((post) => (
              <li key={post.id}>
                <article className="surface-card overflow-hidden">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => navigate(`/discover/${post.id}`)}
                    className="block w-full text-left transition active:scale-[0.99]"
                  >
                    {post.coverImageUrl ? (
                      <div
                        className="aspect-[16/9] bg-clay-100 bg-cover bg-center"
                        style={{ backgroundImage: `url(${post.coverImageUrl})` }}
                        role="img"
                        aria-label={pickTitle(post, locale)}
                      />
                    ) : (
                      <div
                        className="aspect-[16/9] grid place-items-center"
                        style={{
                          background:
                            'linear-gradient(135deg, #FFE3BD 0%, #FFB050 60%, #E5741F 100%)',
                        }}
                        aria-hidden
                      >
                        <span className="font-serif text-4xl font-semibold text-clay-900/70">
                          {(pickTitle(post, locale) || '?').slice(0, 1)}
                        </span>
                      </div>
                    )}
                    <div className="px-5 py-4 space-y-1.5">
                      <h3 className="font-serif text-lg font-semibold text-clay-900 leading-snug line-clamp-2">
                        {pickTitle(post, locale)}
                      </h3>
                      <p className="text-xs text-clay-500">{post.merchantNameZh}</p>
                      <p className="text-sm text-clay-700 line-clamp-3 leading-relaxed">
                        {pickContent(post.contentI18n, locale)}
                      </p>
                    </div>
                  </Button>
                </article>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <BottomNav />
    </main>
  );
}
